/**
 * ReviewSection — display + create reviews for a listing
 *
 * Client component that fetches reviews and allows submitting new ones.
 */

"use client";

import { useEffect, useState, useCallback } from "react";
import { useSession } from "next-auth/react";
import { Star, Send, Loader2 } from "lucide-react";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

interface Review {
    id: string;
    rating: number;
    comment: string | null;
    createdAt: string;
    reviewer: { id: string; name: string | null; image: string | null };
    listing: { id: string; title: string; slug: string } | null;
}

interface ReviewSectionProps {
    /** ID of the user being reviewed (listing owner) */
    revieweeId: string;
    /** Optional listing ID to link review to */
    listingId?: string;
    /** Whether current user is the listing owner (hide form if true) */
    isOwner?: boolean;
}

function StarRating({
    value,
    onChange,
    interactive = false,
}: {
    value: number;
    onChange?: (v: number) => void;
    interactive?: boolean;
}) {
    const [hovered, setHovered] = useState(0);

    return (
        <div className="flex gap-0.5" role="group" aria-label={`Note : ${value} sur 5`}>
            {[1, 2, 3, 4, 5].map((star) => (
                <button
                    key={star}
                    type="button"
                    disabled={!interactive}
                    onClick={() => onChange?.(star)}
                    onMouseEnter={() => interactive && setHovered(star)}
                    onMouseLeave={() => interactive && setHovered(0)}
                    className={`${interactive ? "cursor-pointer hover:scale-110" : "cursor-default"} transition-transform`}
                    aria-label={`${star} étoile${star > 1 ? "s" : ""}`}
                >
                    <Star
                        className={`h-5 w-5 ${star <= (hovered || value)
                                ? "text-hp-orange fill-hp-orange"
                                : "text-hp-gray/20"
                            }`}
                    />
                </button>
            ))}
        </div>
    );
}

export function ReviewSection({ revieweeId, listingId, isOwner }: ReviewSectionProps) {
    const { data: session } = useSession();
    const [reviews, setReviews] = useState<Review[]>([]);
    const [stats, setStats] = useState({ count: 0, average: 0 });
    const [loading, setLoading] = useState(true);

    // Form state
    const [rating, setRating] = useState(0);
    const [comment, setComment] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const fetchReviews = useCallback(async () => {
        try {
            const res = await fetch(`/api/reviews?userId=${revieweeId}`);
            if (res.ok) {
                const data = await res.json();
                setReviews(data.reviews);
                setStats(data.stats);
            }
        } catch {
            /* silent */
        } finally {
            setLoading(false);
        }
    }, [revieweeId]);

    useEffect(() => {
        fetchReviews();
    }, [fetchReviews]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (rating === 0) {
            toast.error("Veuillez sélectionner une note");
            return;
        }

        setSubmitting(true);
        try {
            const res = await fetch("/api/reviews", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    revieweeId,
                    listingId,
                    rating,
                    comment: comment.trim() || undefined,
                }),
            });

            if (!res.ok) {
                const err = await res.json();
                toast.error(err.error || "Erreur lors de l'envoi");
                return;
            }

            toast.success("Avis envoyé !");
            setRating(0);
            setComment("");
            fetchReviews();
        } catch {
            toast.error("Erreur réseau");
        } finally {
            setSubmitting(false);
        }
    };

    // Check if user already reviewed this listing
    const alreadyReviewed = reviews.some((r) => r.reviewer.id === session?.user?.id);
    const canReview =
        session?.user?.id &&
        session.user.id !== revieweeId &&
        !isOwner &&
        !alreadyReviewed;

    return (
        <div className="glass rounded-2xl p-6 space-y-5">
            <div className="flex items-center justify-between">
                <h2 className="text-lg font-bold text-hp-gray">
                    Avis ({stats.count})
                </h2>
                {stats.count > 0 && (
                    <div className="flex items-center gap-2">
                        <StarRating value={Math.round(stats.average)} />
                        <span className="text-sm font-bold text-hp-gray">
                            {stats.average.toFixed(1)}
                        </span>
                    </div>
                )}
            </div>

            {/* Reviews list */}
            {loading ? (
                <div className="flex justify-center py-4">
                    <Loader2 className="h-6 w-6 animate-spin text-hp-purple" />
                </div>
            ) : reviews.length === 0 ? (
                <p className="text-sm text-hp-gray-light text-center py-4">
                    Aucun avis pour l&apos;instant
                </p>
            ) : (
                <div className="space-y-4">
                    {reviews.map((review) => {
                        const initials = (review.reviewer.name ?? "?")
                            .split(" ")
                            .map((w) => w[0])
                            .join("")
                            .toUpperCase()
                            .slice(0, 2);

                        return (
                            <div
                                key={review.id}
                                className="flex gap-3 p-3 rounded-xl bg-white/50 dark:bg-white/5"
                            >
                                <Avatar className="h-9 w-9 shrink-0">
                                    <AvatarFallback className="bg-gradient-to-br from-hp-purple to-hp-orange text-white text-xs font-bold">
                                        {initials}
                                    </AvatarFallback>
                                </Avatar>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="text-sm font-bold text-hp-gray">
                                            {review.reviewer.name ?? "Anonyme"}
                                        </span>
                                        <StarRating value={review.rating} />
                                    </div>
                                    {review.comment && (
                                        <p className="mt-1 text-sm text-hp-gray-light">
                                            {review.comment}
                                        </p>
                                    )}
                                    <p className="mt-1 text-xs text-muted-foreground">
                                        {new Date(review.createdAt).toLocaleDateString("fr-FR", {
                                            day: "numeric",
                                            month: "long",
                                            year: "numeric",
                                        })}
                                    </p>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Review form */}
            {canReview && (
                <form onSubmit={handleSubmit} className="space-y-3 pt-3 border-t border-hp-gray/10">
                    <p className="text-sm font-bold text-hp-gray">Laisser un avis</p>
                    <StarRating value={rating} onChange={setRating} interactive />
                    <Textarea
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                        placeholder="Votre commentaire (optionnel)..."
                        rows={2}
                        maxLength={2000}
                        className="resize-none"
                    />
                    <Button
                        type="submit"
                        disabled={submitting || rating === 0}
                        size="sm"
                        className="bg-gradient-to-r from-hp-purple to-hp-orange text-white shadow-hp rounded-xl"
                    >
                        {submitting ? (
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        ) : (
                            <Send className="mr-2 h-4 w-4" />
                        )}
                        Envoyer
                    </Button>
                </form>
            )}

            {alreadyReviewed && (
                <p className="text-xs text-hp-teal text-center">
                    ✓ Vous avez déjà laissé un avis
                </p>
            )}
        </div>
    );
}
